import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Newspaper } from "lucide-react";
import { API } from "../../constants";
import Footer from "../shared/Footer";
import NewsletterSection from "../shared/NewsletterSection";
import useContentProtection from "../../hooks/useContentProtection";

const BlogPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});
  
  // Apply content protection based on settings
  useContentProtection(settings.disable_dev_tools);
  
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const [postsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/blog-posts?published_only=true`),
          axios.get(`${API}/settings`)
        ]);
        setPosts(postsRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error("Error fetching posts:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-red-500" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div 
        className="relative py-16 md:py-24 bg-cover bg-center"
        style={{ 
          backgroundImage: settings.blog_hero_image 
            ? `url(${settings.blog_hero_image})` 
            : 'linear-gradient(135deg, #dc2626, #ea580c)'
        }}
      >
        {/* Overlay */}
        <div className={`absolute inset-0 ${settings.blog_hero_image ? 'bg-black/50' : 'bg-gradient-to-br from-red-600/90 via-red-500/90 to-orange-500/90'}`} />
        
        <div className="relative max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">News & Blog</h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Stay updated with the latest news, tips, and guides for studying abroad
          </p>
        </div>
      </div>
      
      {/* Blog Posts Grid */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        {posts.length === 0 ? (
          <div className="text-center py-16">
            <Newspaper className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h2 className="text-xl font-semibold text-gray-600 mb-2">No posts yet</h2>
            <p className="text-gray-500">Check back later for updates</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <article 
                key={post.id || post.slug} 
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow cursor-pointer group"
                onClick={() => navigate(`/news/${post.slug}`)}
              >
                {/* Feature Image */}
                <div className="h-48 bg-gray-100 overflow-hidden">
                  {post.feature_image ? (
                    <img 
                      src={post.feature_image} 
                      alt={post.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-100 to-orange-100">
                      <Newspaper className="h-12 w-12 text-red-300" />
                    </div>
                  )}
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded-full">
                      {post.category || 'General'}
                    </span>
                  </div>
                  
                  <h2 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2 group-hover:text-red-600 transition-colors bangla-text">
                    {post.title}
                  </h2>
                  
                  <p className="text-sm text-gray-500 mb-4 line-clamp-3 bangla-text">
                    {post.excerpt || "Read more about this topic..."}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>By {post.author}</span>
                    <span>{new Date(post.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
      
      {/* Newsletter Section */}
      {settings.show_newsletter !== false && (
        <NewsletterSection />
      )}
      
      <Footer />
    </div>
  );
};

export default BlogPage;
